import React, { useEffect, useState } from "react";
import { ric } from "@/lib/ric";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Users, Loader2 } from "lucide-react";

export default function RICWeaverSale() {
  const { lang } = useLang();
  const t = (en, hi) => (lang === "en" ? en : hi);
  const [bins, setBins] = useState([]);
  const [items, setItems] = useState([]);
  const [weavers, setWeavers] = useState([]);
  const [form, setForm] = useState({
    yarn_item_id: "", from_bin_id: "", kg: "", dye_lot: "",
    weaver_id: "", weaver_name_free: "",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    (async () => {
      setBins(await ric.bins());
      setItems(await ric.yarnItems());
      // /weavers is coordinator+ scoped, so this may 403 for staff — silently fall back
      try {
        const r = await api.get("/weavers");
        setWeavers(r.data || []);
      } catch { setWeavers([]); }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setMsg("");
    if (!form.dye_lot) return setMsg(t("Dye lot required for weaver sale", "बुनकर बिक्री में डाई लॉट आवश्यक"));
    if (!form.weaver_id && !form.weaver_name_free) return setMsg(t("Weaver required", "बुनकर आवश्यक"));
    setBusy(true);
    try {
      await ric.weaverSale({ ...form, kg: parseFloat(form.kg) });
      setMsg(t("Sale recorded", "बिक्री दर्ज"));
      setForm({ yarn_item_id: "", from_bin_id: "", kg: "", dye_lot: "", weaver_id: "", weaver_name_free: "" });
    } catch (e) { setMsg(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  return (
    <div data-testid="ric-weaver-sale">
      <h1 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: "var(--brand-navy)", fontFamily: "'Fraunces', serif" }}>
        <Users size={24} /> {t("Weaver sale", "बुनकर बिक्री")}
      </h1>
      <p className="text-xs text-[#7C8E9E] mb-3">
        {t("If the weaver is on the platform and has an active PO, the dye lot goes straight onto the inspector's next first-visit checklist.",
           "यदि बुनकर प्लेटफ़ॉर्म पर है और उसका सक्रिय PO है, तो डाई लॉट सीधे इंस्पेक्टर की चेकलिस्ट पर जाता है।")}
      </p>
      {msg && <div className="mb-3 text-xs bg-amber-50 border border-amber-200 rounded p-2" data-testid="ric-sale-msg">{msg}</div>}

      <form onSubmit={submit} className="hag-surface p-4 space-y-3">
        <Field label={t("Weaver (platform)", "बुनकर (प्लेटफ़ॉर्म)")}>
          <select data-testid="ric-sale-weaver" value={form.weaver_id}
            onChange={(e) => setForm({ ...form, weaver_id: e.target.value })}
            className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm bg-white">
            <option value="">— {t("optional", "वैकल्पिक")} —</option>
            {weavers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </Field>
        <Field label={t("Or weaver name (free text)", "या बुनकर नाम (मुक्त)")}>
          <input data-testid="ric-sale-name-free" value={form.weaver_name_free}
            onChange={(e) => setForm({ ...form, weaver_name_free: e.target.value })}
            className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" />
        </Field>
        <Field label={t("Yarn item", "यार्न")}>
          <select data-testid="ric-sale-item" value={form.yarn_item_id}
            onChange={(e) => setForm({ ...form, yarn_item_id: e.target.value })}
            className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm bg-white">
            <option value="">—</option>
            {items.map((i) => <option key={i.id} value={i.id}>{i.yarn_type} · {i.yarn_count} · {i.colour || "—"} ({i.kg_on_hand} kg)</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("From bin", "बिन")}>
            <select data-testid="ric-sale-bin" value={form.from_bin_id}
              onChange={(e) => setForm({ ...form, from_bin_id: e.target.value })}
              className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm bg-white">
              <option value="">—</option>
              {bins.map((b) => <option key={b.id} value={b.id}>{b.bin_number}</option>)}
            </select>
          </Field>
          <Field label={t("Kg", "कि.")}>
            <input data-testid="ric-sale-kg" type="number" step="0.1" min="0" value={form.kg}
              onChange={(e) => setForm({ ...form, kg: e.target.value })}
              className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" />
          </Field>
        </div>
        <Field label={t("Dye lot (mandatory)", "डाई लॉट (आवश्यक)")}>
          <input data-testid="ric-sale-lot" value={form.dye_lot}
            onChange={(e) => setForm({ ...form, dye_lot: e.target.value })}
            className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" required />
        </Field>
        <button type="submit" data-testid="ric-sale-submit" disabled={busy}
          className="h-11 px-4 rounded-md text-sm font-semibold text-white flex items-center gap-2"
          style={{ background: "var(--brand-navy)" }}>
          {busy && <Loader2 size={14} className="animate-spin" />} {t("Record sale", "बिक्री दर्ज करें")}
        </button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#4A627A] mb-1">{label}</div>
      {children}
    </label>
  );
}
